import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  Button,
  Input,
  Spinner,
  Badge,
} from "reactstrap";
import { APIClient } from "../../helpers/api_helper";
import { toast } from "react-toastify";
import AwesomePagination from "../../Components/Common/AwesomePagination";
import SidebarVoucher from "./SidebarVoucher";
import ModalVoucher from "./ModalVoucher";
import ModalConfirmacao from "./ModalConfirmacao";
import ModalExportVoucher from "./ModalExportVoucher";

const apiClient = new APIClient();

const TabVouchers = () => {
  const [loading, setLoading] = useState(false);
  const [vouchers, setVouchers] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [busca, setBusca] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [campanhas, setCampanhas] = useState([]);
  const [parceiros, setParceiros] = useState([]);
  const [formData, setFormData] = useState({
    tipo: "fixo",
    codigo: "",
    valor: "",
    quantidade: "",
    parceiro_id: "",
    campanha_id: "",
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [voucherAtual, setVoucherAtual] = useState(null);
  const [editForm, setEditForm] = useState({
    quantidade: "",
    valor: "",
  });
  const [modalConfirmacaoOpen, setModalConfirmacaoOpen] = useState(false);
  const [voucherParaExcluir, setVoucherParaExcluir] = useState(null);
  const [modalExportOpen, setModalExportOpen] = useState(false);
  const [exportFormData, setExportFormData] = useState({
    campanha_id: "",
    parceiro_id: "",
  });

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (!modalOpen) {
      setFormData({
        tipo: "fixo",
        codigo: "",
        valor: "",
        quantidade: "",
        parceiro_id: "",
        campanha_id: "",
      });
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    if (!sidebarOpen) {
      setEditForm({
        quantidade: "",
        valor: "",
      });
    }
  };

  const toggleModalConfirmacao = () => {
    setModalConfirmacaoOpen(!modalConfirmacaoOpen);
    if (!modalConfirmacaoOpen) {
      setVoucherParaExcluir(null);
    }
  };

  const toggleExportModal = () => {
    setModalExportOpen(!modalExportOpen);
    if (!modalExportOpen) {
      setExportFormData({
        campanha_id: "",
        parceiro_id: "",
      });
    }
  };

  const fetchVouchers = useCallback(async (currentPage, searchTerm) => {
    setLoading(true);
    const params = {
      itens_pagina: 20,
      pagina: currentPage,
      termo: searchTerm,
    };

    try {
      const response = await apiClient.post(`/vouchers/busca`, params);
      if (response?.sucesso) {
        const dados = response.dados;
        setVouchers(dados.data);
        setTotalPaginas(dados.last_page);
      } else {
        toast.error(response.mensagem || "Erro ao buscar vouchers");
      }
    } catch (error) {
      console.error("Erro completo:", error);
      toast.error("Erro ao buscar vouchers");
    }
    setLoading(false);
  }, []);

  const fetchCampanhas = useCallback(async () => {
    try {
      const response = await apiClient.get("/campanhas-voucher");
      if (response?.sucesso) {
        const dados = response.dados;
        setCampanhas(dados.filter(c => c.ativo));
      }
    } catch (error) {
      console.error("Erro completo:", error);
    }
  }, []);

  const fetchParceiros = useCallback(async () => {
    try {
      const response = await apiClient.get("/parceiros");
      if (response?.sucesso) {
        const dados = response.dados;
        setParceiros(dados);
      }
    } catch (error) {
      console.error("Erro completo:", error);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await fetchVouchers(pagina, busca);
    };
    fetchData();
  }, [pagina, busca, fetchVouchers]);

  useEffect(() => {
    fetchCampanhas();
    fetchParceiros();
  }, [fetchCampanhas, fetchParceiros]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        valor: parseFloat(formData.valor),
        quantidade: parseInt(formData.quantidade),
      };

      const response = await apiClient.post("/campanhas-voucher/criar-vouchers", payload);
      if (response?.sucesso) {
        toast.success(response.mensagem);
        toggleModal();
        fetchVouchers(pagina, busca);
      } else {
        toast.error(response.mensagem || "Erro ao criar voucher");
      }
    } catch (error) {
      console.error("Erro completo:", error);
      toast.error("Erro ao criar voucher");
    }

    setLoading(false);
  };

  const getStatusBadge = (voucher) => {
    if (voucher.quantidade === 0) {
      return <Badge color="danger">Esgotado</Badge>;
    }
    if (voucher.quantidade === voucher.quantidade_original) {
      return <Badge color="secondary">Não Usado</Badge>;
    }
    return <Badge color="success">Ativo</Badge>;
  };

  const handleEditarVoucher = (voucher) => {
    setVoucherAtual(voucher);
    setSidebarOpen(true);
  };

  const handleIncreaseQuantity = async (quantidade) => {
    if (!voucherAtual) return;

    setLoading(true);
    try {
      const response = await apiClient.post(`/vouchers/${voucherAtual.id}/increase`, {
        quantidade: quantidade
      });

      if (response?.sucesso) {
        toast.success("Quantidade aumentada com sucesso!");
        setSidebarOpen(false);
        fetchVouchers(pagina, busca);
      } else {
        toast.error(response.mensagem || "Erro ao aumentar quantidade");
      }
    } catch (error) {
      console.error("Erro completo:", error);
      toast.error("Erro ao aumentar quantidade");
    }
    setLoading(false);
  };

  const handleDecreaseQuantity = async (quantidade) => {
    if (!voucherAtual) return;

    setLoading(true);
    try {
      const response = await apiClient.post(`/vouchers/${voucherAtual.id}/decrease`, {
        quantidade: quantidade
      });

      if (response?.sucesso) {
        toast.success("Quantidade diminuída com sucesso!");
        setSidebarOpen(false);
        fetchVouchers(pagina, busca);
      } else {
        toast.error(response.mensagem || "Erro ao diminuir quantidade");
      }
    } catch (error) {
      console.error("Erro completo:", error);
      toast.error("Erro ao diminuir quantidade");
    }
    setLoading(false);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!voucherAtual) return;

    // Validar se a quantidade nova é maior que a atual
    if (parseInt(editForm.quantidade) <= voucherAtual.quantidade_original) {
      toast.error("A nova quantidade deve ser maior que a quantidade atual");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        quantidade_original: parseInt(editForm.quantidade)
      };

      const response = await apiClient.put(`/vouchers/${voucherAtual.id}`, payload);
      if (response?.sucesso) {
        const dados = response.dados;
        toast.success("Voucher atualizado com sucesso!");
        setSidebarOpen(false);
        fetchVouchers(pagina, busca);
      } else {
        toast.error(response.mensagem || "Erro ao atualizar voucher");
      }
    } catch (error) {
      console.error("Erro completo:", error);
      toast.error("Erro ao atualizar voucher");
    }
    setLoading(false);
  };

  const handleExcluirVoucher = async (voucher) => {
    if (voucher.quantidade !== voucher.quantidade_original) {
      toast.error("Apenas vouchers não utilizados podem ser excluídos");
      return;
    }

    setVoucherParaExcluir(voucher);
    setModalConfirmacaoOpen(true);
  };

  const confirmarExclusao = async () => {
    if (!voucherParaExcluir) return;

    setLoading(true);
    try {
      const response = await apiClient.delete(`/vouchers/${voucherParaExcluir.id}`);
      if (response?.sucesso) {
        toast.success("Voucher excluído com sucesso!");
        setModalConfirmacaoOpen(false);
        fetchVouchers(pagina, busca);
      } else {
        toast.error(response.mensagem || "Erro ao excluir voucher");
      }
    } catch (error) {
      console.error("Erro completo:", error);
      toast.error("Erro ao excluir voucher");
    }
    setLoading(false);
  };

  const handleExportFormChange = (e) => {
    const { name, value } = e.target;
    setExportFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleExportSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const params = {};
    if (exportFormData.campanha_id) params.campanha_id = exportFormData.campanha_id;
    if (exportFormData.parceiro_id) params.parceiro_id = exportFormData.parceiro_id;

    try {
      const response = await apiClient.get("/vouchers/export/csv", params);

      if (response?.sucesso) {
        const csvContent = response.dados;
        const blob = new Blob([csvContent], { type: "text/csv" });
        const blobURL = window.URL.createObjectURL(blob);

        const fileName = `vouchers_${new Date().toISOString().split('T')[0]}.csv`;
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = fileName;
        link.click();

        window.URL.revokeObjectURL(blobURL);
        toggleExportModal();
        toast.success("Arquivo CSV exportado com sucesso!");
      } else {
        toast.error(response.mensagem || "Erro ao exportar vouchers");
      }
    } catch (error) {
      console.error("Erro completo:", error);
      toast.error("Erro ao exportar vouchers");
    }

    setLoading(false);
  };

  return (
    <>
      <Card className="border-0 shadow-sm">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">Vouchers</h4>
            <div className="d-flex gap-2">
              <Button
                color="light"
                className="fw-semibold"
                onClick={toggleExportModal}
              >
                Exportar CSV
              </Button>
              <Button
                color="info"
                className="btn-info text-white fw-semibold"
                onClick={toggleModal}
              >
                Criar Voucher
              </Button>
            </div>
          </div>

          <div className="d-flex gap-2 mb-3">
            <Input
              type="search"
              placeholder="Buscar vouchers..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="w-25"
            />
            <Button
              color="light"
              className="fw-semibold"
              onClick={() => fetchVouchers(1, busca)}
            >
              Buscar
            </Button>
          </div>

          <div className="table-responsive">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="info" />
              </div>
            ) : (
              <table className="table align-middle table-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Código</th>
                    <th scope="col">Campanha</th>
                    <th scope="col">Parceiro</th>
                    <th scope="col">Valor</th>
                    <th scope="col">Status</th>
                    <th scope="col">Disponíveis</th>
                    <th scope="col">Total</th>
                    <th scope="col">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {vouchers.map((voucher) => (
                    <tr key={voucher.id}>
                      <td>
                        <div className="fw-medium">{voucher.codigo}</div>
                      </td>
                      <td>
                        {campanhas.find((c) => c.id === voucher.campanha_id)?.nome || "-"}
                      </td>
                      <td>
                        {parceiros.find((p) => p.id === voucher.parceiro_id)?.nome || "-"}
                      </td>
                      <td>
                        {voucher.valor
                          ? `R$ ${parseFloat(voucher.valor).toFixed(2)}`
                          : "-"}
                      </td>
                      <td>{getStatusBadge(voucher)}</td>
                      <td>{voucher.quantidade}</td>
                      <td>{voucher.quantidade_original}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            color="light"
                            size="sm"
                            className="btn-icon"
                            onClick={() => handleEditarVoucher(voucher)}
                          >
                            <i className="ri-pencil-line"></i>
                          </Button>
                          {voucher.quantidade === voucher.quantidade_original && (
                            <Button
                              color="danger"
                              size="sm"
                              className="btn-icon"
                              onClick={() => handleExcluirVoucher(voucher)}
                            >
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <AwesomePagination
            pagina={pagina}
            totalPaginas={totalPaginas}
            setPagina={setPagina}
          />
        </CardBody>
      </Card>

      <SidebarVoucher
        isOpen={sidebarOpen}
        toggle={toggleSidebar}
        voucherAtual={voucherAtual}
        loading={loading}
        campanhas={campanhas}
        parceiros={parceiros}
        getStatusBadge={getStatusBadge}
        onIncreaseQuantity={handleIncreaseQuantity}
        onDecreaseQuantity={handleDecreaseQuantity}
      />

      <ModalVoucher
        isOpen={modalOpen}
        toggle={toggleModal}
        formData={formData}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        loading={loading}
        campanhas={campanhas}
        parceiros={parceiros}
      />

      <ModalConfirmacao
        isOpen={modalConfirmacaoOpen}
        toggle={toggleModalConfirmacao}
        onConfirm={confirmarExclusao}
        loading={loading}
        title="Excluir Voucher"
        message="Tem certeza que deseja excluir este voucher? Esta ação não pode ser desfeita."
      />

      <ModalExportVoucher
        isOpen={modalExportOpen}
        toggle={toggleExportModal}
        formData={exportFormData}
        handleChange={handleExportFormChange}
        handleSubmit={handleExportSubmit}
        loading={loading}
        campanhas={campanhas}
        parceiros={parceiros}
      />
    </>
  );
};

export default TabVouchers; 